import { createRng } from '../../core/rng';
import { ACCENT_RANK } from '../../core/track-chart';
import type { Accent, AccentKind, TrackCalendar } from '../../core/track-chart';
import type { Rng } from '../../core/rng';
import type { MixOptions } from './kit';
import type { Note, Section } from './song';

export type Params = { bpm: number; bars?: number; seconds?: number; energy: number };

export type Ev = {
  v: string;
  t: number;
  bar: number;
  step: number;
  vel: number;
  dur?: number;
  note?: string;
  notes?: readonly string[];
  strum?: number;
  glide?: { to: string; at: number; time: number };
  end?: number;
};

export type Auto = { v: string; points: [t: number, value: number][] };

export type Score = {
  id: string;
  template: string;
  seed: string;
  params: Params;
  title: string;
  genre: string;
  bpm: number;
  swing: number;
  lufs: number;
  key: number;
  form: Section[];
  mix: MixOptions;
  events: Ev[];
  autos: Auto[];
  accents: Readonly<Partial<Record<string, AccentKind>>>;
};

export type Template = {
  id: string;
  genre: string;
  bpm: readonly [min: number, max: number];
  ref: { seed: string; title: string; params: Params };
  score(seed: string, params: Params): Score;
};

export type Part = { name: string; bars: number; min: number; rank: number; with?: string };

export type FormPlan = { intro: number; outro: number; body: readonly Part[] };

export type Cell = readonly [step: number, len: number, vel?: number];

export type Harmony = readonly { at: number; tones: readonly number[] }[];

const NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
const LETTER: Readonly<Record<string, number>> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

export function midi(note: string): number {
  const m = /^([A-G])(#|b)?(-?\d+)$/.exec(note);
  if (m === null) throw new Error(`bad note ${note}`);
  const acc = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  return (Number(m[3]) + 1) * 12 + LETTER[m[1]!]! + acc;
}

export const pc = (n: number): number => ((n % 12) + 12) % 12;

export const noteName = (n: number): string => `${NAMES[pc(n)]}${Math.floor(n / 12) - 1}`;

export const transpose = (note: string, semis: number): string => (semis === 0 ? note : noteName(midi(note) + semis));

export const pcs = (notes: readonly string[]): number[] => [...new Set(notes.map((n) => pc(midi(n))))];

export const cycle = (i: number, n: number): number => ((i % n) + n) % n;

export function barsOf(p: Params): number {
  const raw = p.bars ?? ((p.seconds ?? 60) * p.bpm) / 240;
  return Math.max(8, Math.round(raw / 4) * 4);
}

export function fitForm(total: number, plan: FormPlan): Section[] {
  const [intro, outro] = total <= 24 ? [2, 2] : total < 40 ? [4, 4] : [plan.intro, plan.outro];
  let budget = total - intro - outro;
  const ranked = [...plan.body].sort((a, b) => a.rank - b.rank);
  const size = new Map<Part, number>();
  for (const p of ranked) {
    if (p.min > budget) continue;
    size.set(p, p.min);
    budget -= p.min;
  }
  for (const p of ranked) {
    if (p.with === undefined || !size.has(p) || [...size.keys()].some((q) => q.name === p.with)) continue;
    budget += size.get(p)!;
    size.delete(p);
  }
  if (size.size === 0) {
    size.set(ranked[0]!, budget);
    budget = 0;
  }
  for (let full = false; budget > 0; ) {
    let grew = false;
    for (const p of ranked) {
      const cur = size.get(p);
      if (cur === undefined || budget <= 0 || (!full && cur >= p.bars)) continue;
      const add = Math.min(4, budget, full ? 4 : p.bars - cur);
      size.set(p, cur + add);
      budget -= add;
      grew = true;
    }
    if (!grew) full = true;
  }
  const body = plan.body.filter((p) => size.has(p)).map((p) => ({ name: p.name, bars: size.get(p)! }));
  return [{ name: 'intro', bars: intro }, ...body, { name: 'outro', bars: outro }];
}

export function find(form: readonly Section[], name: string): number | undefined {
  let bar = 0;
  for (const s of form) {
    if (s.name === name) return bar;
    bar += s.bars;
  }
  return undefined;
}

export const sizeOf = (form: readonly Section[], name: string): number => form.find((s) => s.name === name)?.bars ?? 0;

export const totalBars = (form: readonly Section[]): number => form.reduce((n, s) => n + s.bars, 0);

export const titleOf = (seed: string): string =>
  seed
    .split('-')
    .filter((w) => w.length > 0)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');

export function trackId(template: Template, seed: string, p: Params): string {
  const r = template.ref;
  const exact = seed === r.seed && p.bpm === r.params.bpm && barsOf(p) === barsOf(r.params) && p.energy === r.params.energy;
  return exact ? `${template.id}-${seed}` : `${template.id}-${seed}-${p.bpm}b${barsOf(p)}e${Math.round(p.energy * 100)}`;
}

export type Chooser = { readonly orig: boolean; readonly rng: Rng; pick<T>(items: readonly T[]): T };

export function chooser(template: Template, seed: string): Chooser {
  const orig = seed === template.ref.seed;
  const rng = createRng(seed).fork(template.id);
  return { orig, rng, pick: (items) => (orig ? items[0]! : rng.pick(items)) };
}

export function weighted<T>(rng: Rng, items: readonly T[], weights: readonly number[]): T {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = rng.float() * total;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i]!;
    if (r < 0) return items[i]!;
  }
  return items[items.length - 1]!;
}

export function shuffle<T>(rng: Rng, items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = rng.int(0, i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export const cellsOf = (notes: readonly Note[]): Cell[] => notes.map(([step, , len, vel]) => (vel === undefined ? [step, len] : [step, len, vel]));

export function blend(scale: readonly number[], tones: readonly number[]): number[] {
  const alien = tones.filter((t) => !scale.includes(t));
  const out = scale.filter((p) => !alien.some((t) => pc(p - t) === 1 || pc(t - p) === 1));
  for (const t of tones) if (!out.includes(t)) out.push(t);
  return out;
}

export function tune(
  rng: Rng,
  cells: readonly Cell[],
  harmony: Harmony,
  scale: readonly number[],
  range: readonly [number, number],
  prev: number,
  cadence: boolean,
): { notes: Note[]; last: number } {
  const notes: Note[] = [];
  let last = prev;
  let same = 0;
  cells.forEach(([step, len, vel], i) => {
    const tones = [...harmony].reverse().find((h) => h.at <= step)?.tones ?? harmony[0]!.tones;
    const strong = step % 8 === 0 || len >= 4 || (cadence && i === cells.length - 1);
    const pool = strong ? tones : blend(scale, tones);
    const options: number[] = [];
    for (let m = range[0]; m <= range[1]; m++) if (pool.includes(pc(m))) options.push(m);
    const weights = options.map((m) => {
      const d = Math.abs(m - last);
      if (d === 0) return same > 0 ? 0.05 : 0.4;
      return d <= 2 ? 1 : d <= 4 ? 0.75 : d <= 7 ? 0.3 : 0.04;
    });
    const m = weighted(rng, options, weights);
    same = m === last ? same + 1 : 0;
    last = m;
    notes.push(vel === undefined ? [step, noteName(m), len] : [step, noteName(m), len, vel]);
  });
  return { notes, last };
}

export function phrase(
  rng: Rng,
  rhythms: readonly (readonly Cell[])[],
  harmonies: readonly Harmony[],
  scale: readonly number[],
  range: readonly [number, number],
  start: number,
): Note[][] {
  const a = rng.int(0, rhythms.length);
  const b = rhythms.length > 1 ? (a + 1 + rng.int(0, rhythms.length - 1)) % rhythms.length : a;
  const c = rng.int(0, rhythms.length);
  const plan = harmonies.map((_, i) => (i >= 4 && i % 4 === 2 ? rng.int(0, rhythms.length) : [a, b, a, c][i % 4]!));
  const out: Note[][] = [];
  let prev = start;
  harmonies.forEach((h, i) => {
    const r = tune(rng, rhythms[plan[i]!]!, h, scale, range, prev, i % 4 === 3);
    out.push(r.notes);
    prev = r.last;
  });
  return out;
}

export const LEAD = 0.5;

export function calendarOf(score: Score): TrackCalendar {
  const perBeat = 60 / score.bpm;
  const bySlot = new Map<number, Accent & { t: number }>();
  for (const e of score.events) {
    const kind = score.accents[e.v];
    if (kind === undefined) continue;
    const beat = (e.t - LEAD) / perBeat;
    const slot = Math.round(beat * 4);
    const had = bySlot.get(slot);
    if (had === undefined) {
      bySlot.set(slot, { beat, slot, kind, strength: e.vel, t: e.t });
      continue;
    }
    const strength = Math.max(had.strength, e.vel);
    if (ACCENT_RANK[kind] < ACCENT_RANK[had.kind]) bySlot.set(slot, { beat, slot, kind, strength, t: e.t });
    else had.strength = strength;
  }
  const accents = [...bySlot.values()]
    .sort((a, b) => a.slot - b.slot)
    .map(({ beat, slot, kind, strength }) => ({ beat, slot, kind, strength: Math.min(1, strength) }));
  let bar = 0;
  const form = score.form.map((s) => {
    const part = { name: s.name, bar, bars: s.bars };
    bar += s.bars;
    return part;
  });
  return { bpm: score.bpm, lead: LEAD, beats: bar * 4, form, accents };
}
